// @flow

import * as React from 'react';
import DropDownInput from "./DropDownInput";
import NumberInput from "./NumberInput";
import TubesInput from "./TubesInput";
import OptionManager from "../common/OptionManager";

import type { NumTubesCallback } from "./TubesInput"
import DependencyManager from "../common/DependencyManager";
import type {ComponentMeta, UnmountHandler} from "../common/CommonTypes";
import CalculationSectionHeader from "./CalculationSectionHeader";
import CalculationSectionSubHeader from "./CalculationSectionSubHeader";
import ReactAI from 'react-appinsights';

type Props = {
    optionManager: OptionManager,
    dependencyManager: DependencyManager,

    onChange: (event: SyntheticEvent<>) => void,
    onBlur: (event: SyntheticEvent<>) => void,
    onUnmount: UnmountHandler,
    addTube: NumTubesCallback,
    removeTube: NumTubesCallback,

    shaftDepthM: number,
    slopeAngleN1: number,
    shaftCapacityM3PerHour: number,
    shaftLengthM: number,

    shaftDepthMMeta: ComponentMeta,
    slopeAngleN1Meta: ComponentMeta,
    shaftCapacityM3PerHourMeta: ComponentMeta,
    shaftLengthMMeta: ComponentMeta,

    numTubes: number,
    numTubesName: string,
    tubeValuesNamePrefix: string,
    tubeValues: {[valueName: string]: number},
    tubeMetas: {[valueName: string]: ComponentMeta},
    
    minShaftLength: number,

    techniquesToCompare: number,

    phaseNr: number,

};

class CalculationTraditionalDiggingInput extends React.Component<Props> {

    render() {

        const optionManager = this.props.optionManager;
        const dependencyManager = this.props.dependencyManager;

        const onChange = this.props.onChange;
        const onBlur = this.props.onBlur;
        const onUnmount = this.props.onUnmount;
        const addTube = this.props.addTube;
        const removeTube = this.props.removeTube;

        const shaftDepthM = this.props.shaftDepthM;
        const slopeAngleN1 = this.props.slopeAngleN1;
        const shaftCapacityM3PerHour = this.props.shaftCapacityM3PerHour;
        const shaftLengthM = this.props.shaftLengthM;

        const shaftDepthMMeta = this.props.shaftDepthMMeta;
        const slopeAngleN1Meta = this.props.slopeAngleN1Meta;
        const shaftCapacityM3PerHourMeta = this.props.shaftCapacityM3PerHourMeta;
        const shaftLengthMMeta = this.props.shaftLengthMMeta;

        const numTubes = this.props.numTubes;
        const numTubesName = this.props.numTubesName;
        const tubeValuesNamePrefix = this.props.tubeValuesNamePrefix;
        const tubeValues = this.props.tubeValues;

        let tubeTypes = optionManager.availableSelections.tubeType;
        let maxTubes = 3;
        if(this.props.techniquesToCompare === optionManager.ids.TECH_PUL) {
            tubeTypes = optionManager.availableSelections.tubeTypePul;
            maxTubes = tubeTypes.length;
        }

        return (

            <div className="calculation-traditional-digging-info input-section">
                <hr className="print-hide section-delimiter"/>

                <CalculationSectionHeader text="Traditionell schakt" smallText="och utbyte av rör" extraClass="page-break" id={ `trad-${this.props.phaseNr}` }/>

                <div className="input-section-content">
                    <CalculationSectionSubHeader text="Nya rör"/>
                    <TubesInput numTubes={numTubes}
                                namePrefix={tubeValuesNamePrefix}
                                minTubes={1}
                                maxTubes={maxTubes}
                                values={tubeValues}
                                metas={this.props.tubeMetas}
                                isNoDig={false}
                                phaseNr={this.props.phaseNr}
                                tubeTypes={tubeTypes}
                                tubeMaterialCallback={dependencyManager.getTubeMaterialsTrad.bind(dependencyManager)}
                                tubeDiameterCallback={dependencyManager.getTubeDiametersTrad.bind(dependencyManager)}
                                onChange={onChange}
                                onBlur={onBlur}
                                onUnmount={onUnmount}
                                numTubesName={numTubesName}
                                addTube={addTube}
                                removeTube={removeTube} />
    
                    <CalculationSectionSubHeader text="Traditionell schaktning"/>
                    
                    <div className="row">
    
                    <NumberInput    htmlName="shaftDepthM"
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    onUnmount={onUnmount}
                                    extraClassFormGroup="col-sm-6"
                                    labelText="Schaktdjup (m)"
                                    phaseNr={this.props.phaseNr}
                                    helpHtml='<img src="/images/shaft-params.png"/><br/><p>Välj ett schaktdjup som passar för det rör som ska ligga underst</p>'
                                    value={shaftDepthM}
                                    meta={shaftDepthMMeta}
                                    step={0.1}
                                    min={0} />
    
                    <DropDownInput htmlName="slopeAngleN1"
                                   onChange={onChange}
                                   onBlur={onBlur}
                                   onUnmount={onUnmount}
                                   extraClassFormGroup="col-sm-6"
                                   labelText="Släntlutning (N:1)"
                                   phaseNr={this.props.phaseNr}
                                   helpHtml='<img src="/images/shaft-params.png"/><br/><p>Välj en slätlutning som passar för schakten:<br/>1 = 1:1 bred schakt, <br/>5 = rak vägg med spont.</p>'
                                   value={slopeAngleN1}
                                   meta={slopeAngleN1Meta}
                                   includeEmptyOption={false}
                                   values={optionManager.availableSelections.slopeAngle} />
                    </div>
    
                    <div className="row">
                        
                        <NumberInput    htmlName="shaftCapacityM3PerHour"
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    onUnmount={onUnmount}
                                    extraClassFormGroup="col-sm-6"
                                    labelText="Schaktkapacitet (m³/tim)"
                                        phaseNr={this.props.phaseNr}
                                        helpHtml='Schaktkapaciteten beror på platsen och dess markförhållanden. I stadsmiljö är kapaciteten vanligtvis lägre (ex. 5 m³/tim) medan den kan vara högre på ängsmark (ex. 20 m³/tim).'
                                    value={shaftCapacityM3PerHour}
                                    meta={shaftCapacityM3PerHourMeta}
                                    min={0} />
    
                    <NumberInput    htmlName="shaftLengthM"
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    onUnmount={onUnmount}
                                    extraClassFormGroup="col-sm-6"
                                    labelText="Schaktlängd (m)"
                                    phaseNr={this.props.phaseNr}
                                    helpHtml='Anpassa schaktlängden. Vanligtvis är schakten längre än själva rörlängden för bättre åtkomst eller för att kunna utföra andra åtgärder.'
                                    value={shaftLengthM}
                                    meta={shaftLengthMMeta}
                                    min={this.props.minShaftLength} />
                    </div>
    
                </div>
                <hr className="print-hide section-delimiter"/>
            </div>
        );

    }

}

export default ReactAI.withTracking(CalculationTraditionalDiggingInput);



// WEBPACK FOOTER //
// ./src/components/CalculationTraditionalDiggingInput.js